import ConfirmPopover from '@/components/common/ConfirmPopover';
import DataTypeSelect from '@/components/common/DataTypeSelect';
import ListArrows from '@/components/common/ListArrows';
import TextFieldWrapper from '@/components/common/TextFieldWrapper';
import { useMetadata } from '@/hooks/useMetadata';
import { Arg } from '@/types/subroutine';
import { Severity } from '@/types/validation';
import { VariableScope } from '@/types/variable';
import { isNumber } from '@/util/typeGuards';
import { getScopeLabel } from '@/util/variables';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';
import RouteIcon from '@mui/icons-material/Route';
import SettingsPhoneIcon from '@mui/icons-material/SettingsPhone';
import TaskIcon from '@mui/icons-material/Task';
import { Box, Chip, FormControl, Stack, Tooltip, Typography } from '@mui/material';
import { FC } from 'react';

type ArgProps = {
  value: Arg;
  binding?: string;
  type?: string;
  variableLabel?: string;
  scope?: VariableScope;
  data?: Arg[];
  index?: number;
  onArrowClick?: (data: Arg[], arrowType: string) => void;
  handleChange: (label: string) => void;
  onTypeChange: (type: string) => void;
  onClick?: () => void;
  onDelete?: () => void;
  onChipDelete?: () => void;
  disableFields?: boolean;
  error?: Severity;
};

const ArgChip: FC<ArgProps> = (props) => {
  const { metadata } = useMetadata();

  const getScopeIcon = () => {
    switch (props.scope) {
      case 'global':
        return <PublicIcon />;
      case 'task':
        return <TaskIcon />;
      case 'session':
        return <SettingsPhoneIcon />;
      case 'channel':
        return <RouteIcon />;
      case 'local':
        return <PlaceIcon />;
      default:
        return <DataObjectIcon />;
    }
  };

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      gap={1}
      width='100%'
      {...(props.error && { color: props.error + '.main' })}
    >
      <TextFieldWrapper
        size='small'
        value={props.value?.label}
        placeholder='label'
        onChange={(e) => props.handleChange(e.currentTarget.value)}
        sx={{ minWidth: 100, maxWidth: '50%', mr: 2, flexGrow: 1 }}
        disableFields={props.disableFields}
      />
      {!props.binding && (
        <Typography variant='body1' fontStyle='italic' color='text.disabled'>
          No argument set
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip
          title={
            (props.scope || props.variableLabel) && (
              <Box>
                {props.scope && <Box>{`Scope: ${getScopeLabel(props.scope, metadata)}`}</Box>}
                {props.variableLabel && <Box>{`Type: ${props.variableLabel}`}</Box>}
              </Box>
            )
          }
          placement='top'
          arrow
        >
          <Chip
            icon={getScopeIcon()}
            deleteIcon={props.binding ? <CloseIcon fontSize='small' /> : <ArrowDropDownIcon />}
            label={props.binding || 'Pick one'}
            onDelete={props.binding ? props.onChipDelete : props.onClick}
            onClick={props.onClick}
            sx={{
              fontFamily: 'monospace',
              fontSize: '1em',
              fontWeight: 700,
              pl: 0.5,
              pr: 0.5,
              mr: 1
            }}
            size='small'
            color='secondary'
          />
        </Tooltip>
        <FormControl size='small' sx={{ mr: 1 }}>
          <DataTypeSelect
            value={props.type || ''}
            typelist='argument'
            disabled={props.disableFields}
            onChange={props.onTypeChange}
          />
        </FormControl>
        {isNumber(props.index) && props.data && props.onArrowClick && (
          <ListArrows data={props.data} index={props.index} onChange={props.onArrowClick} />
        )}
        <ConfirmPopover
          text={
            <>
              Remove <code>{props.value?.label}</code>?
            </>
          }
          icon={<ClearIcon />}
          onConfirm={() => props.onDelete && props.onDelete()}
          skipConfirm={!props.value.label && !props.value.type && !props.binding}
        />
      </Box>
    </Stack>
  );
};

export default ArgChip;
